import { Box, Typography } from '@mui/material'
import { CheckCircleIcon } from '@untitled-theme/icons-react'

import type { AdditionalService } from '../types/additional-services.types'

// Mirrors the FundingTable styling of the Förderungen section, laid out vertically
interface ServiceBlockProps {
  service: AdditionalService
}

const ServiceBlock = ({ service }: ServiceBlockProps) => {
  const { title, subtitle, icon: Icon, bullets, pricing } = service

  return (
    <Box
      component="article"
      aria-labelledby={`service-${service.id}-title`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #E1E1E4',
        borderRadius: '16px',
        overflow: 'hidden',
        backgroundColor: '#ffffff',
      }}
    >
      {/* Header strip, like the table head */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '16px 20px',
          backgroundColor: '#FAFAFB',
          borderBottom: '1px solid #E1E1E4',
        }}
      >
        <Box
          sx={{
            width: '32px',
            height: '32px',
            borderRadius: '8px',
            backgroundColor: '#E5F5E7',
            color: '#5BC06B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg': { width: '18px', height: '18px' },
          }}
        >
          <Icon aria-hidden="true" />
        </Box>
        <Box>
          <Typography
            id={`service-${service.id}-title`}
            component="h2"
            sx={{ fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}
          >
            {title}
          </Typography>
          <Typography
            sx={{
              fontWeight: 400,
              fontSize: '11px',
              lineHeight: '16px',
              color: '#6A6E78',
              marginTop: '2px',
            }}
          >
            {subtitle}
          </Typography>
        </Box>
      </Box>

      {/* Body, like a table row */}
      <Box sx={{ padding: '20px', flex: 1 }}>
        <Box component="ul" sx={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {bullets.map(bullet => (
            <Box
              component="li"
              key={bullet}
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                '&:not(:last-of-type)': { marginBottom: '12px' },
              }}
            >
              <CheckCircleIcon
                style={{ width: 16, height: 16, color: '#5BC06B', flexShrink: 0, marginTop: 1 }}
              />
              <Typography
                sx={{ fontSize: '12px', fontWeight: 500, lineHeight: '18px', color: '#464A54' }}
              >
                {bullet}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Footer row with pricing pill */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '16px 20px',
          borderTop: '1px solid #E1E1E4',
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            backgroundColor: '#E5F5E7',
            padding: '6px 10px',
            borderRadius: '16px',
            fontWeight: 600,
            fontSize: '11px',
            lineHeight: '15px',
            color: '#181D29',
            whiteSpace: 'nowrap',
          }}
        >
          {pricing.label}
        </Box>
        {pricing.detail && (
          <Typography sx={{ fontWeight: 400, fontSize: '10px', color: '#6A6E78' }}>
            {pricing.detail}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default ServiceBlock
